const DAY = 86400;
const dateLabel = time => new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(time * 1000);
const priceEqual = (left, right) => Math.abs(left - right) <= Math.max(0.000051, Math.abs(left) * Number.EPSILON * 4);

export function engulfingMonth(time) {
    if (!Number.isFinite(time)) return null;
    const date = new Date(time * 1000);
    if (!Number.isFinite(date.getTime())) return null;
    const start = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1) / 1000;
    const end = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1) / 1000;
    return { start, end, label: new Intl.DateTimeFormat('es-ES', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(start * 1000) };
}

const validBar = bar => bar && Number.isFinite(bar.time)
    && ['open', 'high', 'low', 'close'].every(key => Number.isFinite(bar[key]) && bar[key] > 0)
    && bar.low <= Math.min(bar.open, bar.close) && bar.high >= Math.max(bar.open, bar.close);

// Eurekers outside-range bars, not candlestick-body engulfing patterns.
// Only preceding history contributes to ATH; never future bars or rounded labels.
export function detectMonthlyEngulfingBars(candles, { resolution = 'M', hasFullHistory = false, now = Date.now() / 1000 } = {}) {
    if (resolution !== 'M' || !hasFullHistory) return [];
    const result = [];
    let maximum = -Infinity;
    let previous = null;
    for (const bar of candles) {
        const month = engulfingMonth(bar?.time);
        // A malformed, unordered or missing monthly period breaks ATH evidence.
        if (!validBar(bar) || !month || (previous && engulfingMonth(previous.time).end !== month.start)) break;
        if (month.start > now) break;
        if (previous && bar.high > previous.high && bar.low < previous.low && bar.high >= maximum) {
            result.push({ id: `engulfing-${month.start}`, time: bar.time, monthStart: month.start, monthEnd: month.end,
                monthLabel: month.label, bar: { ...bar }, previous: { ...previous }, provisional: now < month.end });
        }
        maximum = Math.max(maximum, bar.high);
        previous = bar;
    }
    return result;
}

const weekStart = time => {
    const date = new Date(time * 1000);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() - (date.getUTCDay() + 6) % 7) / 1000;
};

const aggregate = bars => ({ time: bars[0].time, open: bars[0].open, high: Math.max(...bars.map(bar => bar.high)),
    low: Math.min(...bars.map(bar => bar.low)), close: bars[bars.length - 1].close });

export function buildEngulfingDetail(candidate, candles = []) {
    const unknown = (reason, explanation, weeks = []) => ({ kind: 'unknown', label: 'Tipo no determinado', reason, explanation, weeks, events: [] });
    const days = candles.filter(bar => bar?.time >= candidate.monthStart && bar.time < candidate.monthEnd).sort((a, b) => a.time - b.time);
    if (!days.length) return unknown('empty', 'No hay datos diarios disponibles para desplegar este mes.');
    if (days.some((bar, index) => !validBar(bar) || (index && Math.floor(bar.time / DAY) === Math.floor(days[index - 1].time / DAY)))) {
        return unknown('invalid', 'Los datos de este tramo son incompletos o inconsistentes. No se puede determinar el tipo.');
    }
    const groups = new Map();
    for (const bar of days) {
        const start = weekStart(bar.time);
        if (!groups.has(start)) groups.set(start, []);
        groups.get(start).push(bar);
    }
    const weeks = [...groups].map(([start, bars]) => ({ ...aggregate(bars), start,
        firstSession: bars[0].time, lastSession: bars[bars.length - 1].time,
        label: `${dateLabel(bars[0].time)} – ${dateLabel(bars[bars.length - 1].time)}`,
        partial: start < candidate.monthStart || start + 7 * DAY > candidate.monthEnd }));
    const monthly = aggregate(days);
    if (!['open', 'high', 'low', 'close'].every(key => priceEqual(monthly[key], candidate.bar[key]))) {
        return unknown('mismatch', 'El desglose diario no coincide con la barra mensual. Mostramos los datos disponibles sin asignar un tipo.', weeks);
    }
    const firstLowBreak = days.findIndex(bar => bar.low < candidate.previous.low);
    const firstHighBreak = days.findIndex(bar => bar.high > candidate.previous.high);
    const lowDays = days.filter(bar => priceEqual(bar.low, monthly.low));
    const highDays = days.filter(bar => priceEqual(bar.high, monthly.high));
    const breakOrder = firstLowBreak < firstHighBreak ? 'fast' : firstLowBreak > firstHighBreak ? 'slow' : null;
    // Repeated extrema must have unambiguous order too. An OHLC bar cannot tell
    // which of its extremes came first, even at daily resolution.
    const extremeOrder = lowDays[lowDays.length - 1].time < highDays[0].time ? 'fast'
        : highDays[highDays.length - 1].time < lowDays[0].time ? 'slow' : null;
    if (!breakOrder || !extremeOrder || firstLowBreak < 0 || firstHighBreak < 0) {
        return unknown('ambiguous', 'Los datos diarios no permiten ordenar los movimientos sin ambigüedad: coinciden en una sesión o hay extremos repetidos.', weeks);
    }
    if (breakOrder !== extremeOrder) {
        return unknown('criteria-disagree', 'El orden de ruptura del rango anterior y el de los extremos finales no coinciden. Clasificación pendiente de validar el criterio del método.', weeks);
    }
    const events = [
        { label: 'Mínimo del mes', time: lowDays[0].time, price: monthly.low },
        { label: 'Máximo del mes', time: highDays[0].time, price: monthly.high }
    ].sort((a, b) => a.time - b.time).map(event => ({ ...event, dateLabel: dateLabel(event.time) }));
    return { kind: breakOrder, label: breakOrder === 'fast' ? 'Envolvente rápida' : 'Envolvente lenta', weeks, events,
        explanation: breakOrder === 'fast'
            ? 'Primero pierde el mínimo anterior y después supera el máximo. El mínimo final también precede al máximo final.'
            : 'Primero supera el máximo anterior y después pierde el mínimo. El máximo final también precede al mínimo final.' };
}
